import { Helmet } from 'react-helmet-async';
import AdminManagementView from './components/AdminManagementView';
import {
  PageTitleWrapper,
  PageHeader,
  SimpleDialog,
  CustomForm,
  showErrorMessage
} from 'src/shared/components/index';
import {
  Grid,
  Container,
  Box,
  Typography,
  Button,
  TextField,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { useEffect, useState, MouseEvent } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  getAdminData,
  updateAdminData,
  deleteAdmin,
  addAdminUser,
  setIsAdminListUpdated,
  updateAdminUserStatus,
  setIsError,
  selectAdminManagementState
} from './services/adminManagement.service';
import {
  getOrganizationData,
  selectOrganizationState
} from '../../modules/Organization/services/organization.service';
import AddTwoToneIcon from '@mui/icons-material/AddTwoTone';
import { useTranslation } from 'react-i18next';
import { FormFieldType } from 'src/shared/components/form-field/service/formFieldInterface';
import {
  ADMIN_ROLES,
  I18N,
  LIST_VIEW,
  REGEX,
  ROLES
} from 'src/shared/constants/constants';
import { PageHeaderFieldType } from 'src/shared/components/page-header/service/pageHeaderFieldInterface';
import GridViewSharpIcon from '@mui/icons-material/GridViewSharp';
import TableRowsTwoToneIcon from '@mui/icons-material/TableRowsTwoTone';

const AdminManagement = () => {
  const [selectedTab, setSelectedTab] = useState<string | null>(
    LIST_VIEW.TABLE_VIEW
  );
  const [open, setOpen] = useState<boolean>(false);
  const [isAddModal, setIsAddModal] = useState<boolean>(false);
  const { i18n, t } = useTranslation(I18N.ADMIN_USER);
  const dispatch = useDispatch<any>();
  const [adminList, setAdminList] = useState([]);
  const { adminData, isAdminListUpdated, isError, errorMessage } = useSelector(
    selectAdminManagementState
  );
  const { organizationData } = useSelector(selectOrganizationState);
  const [organizationsList, setOrganizationsList] = useState([]);
  const [fields, setFields] = useState([] as FormFieldType[]);
  const [initialValues, setInitialValues] = useState({
    email: '',
    name: '',
    phoneNo: '',
    role: '',
    tenants: []
  });

  useEffect(() => {
    dispatch(getAdminData());
    dispatch(getOrganizationData());
    setFields(baseCustomFormFields);
  }, []);

  useEffect(() => {
    if (isError) {
      dispatch(setIsError(false));
      if (errorMessage !== '') {
        return showErrorMessage(errorMessage, {
          theme: 'colored',
          position: 'top-right',
          autoClose: 5000
        });
      }
    }
  }, [isError]);

  const handleInputChange = (event, field) => { };

  const handleRoleChange = (event, prevField) => {
    if (prevField.value !== ROLES.ADMIN_OPERATOR.toString) {
      const fieldList = [...baseCustomFormFields, ...orgCustomFormFields];
      setFields(fieldList);
    } else {
      const fieldList = [...baseCustomFormFields];
      setFields(fieldList);
    }
  };

  const baseCustomFormFields: FormFieldType[] = [
    {
      id: 'email',
      name: 'email',
      type: 'email',
      label: 'Email',
      validations: {
        required: true,
        regex: REGEX.EMAIL
      },
      errorMessages: {
        requiredErrMsg: 'email is required',
        regexErrMsg: 'email is not valid'
      },
      handleFieldChange: handleInputChange
    },
    {
      id: 'name',
      name: 'name',
      type: 'multi-text',
      label: 'Name',
      validations: {
        required: true,
        maxLength: 50
      },
      errorMessages: {
        requiredErrMsg: 'name is required',
        regexErrMsg: 'name is not valid'
      },
      handleFieldChange: handleInputChange
    },
    {
      id: 'phoneNo',
      name: 'phoneNo',
      type: 'regex',
      label: t('booking.mobile'),
      validations: {
        regex: /[789][0-9]{9}/,
        minLength: 10,
        maxLength: 10,
      },
    },
    {
      id: 'role',
      name: 'role',
      type: 'select',
      label: 'Role',
      options: ADMIN_ROLES,
      itemValueKey: 'id',
      itemLabelKey: 'roleName',
      validations: {
        required: true
      },
      errorMessages: {
        requiredErrMsg: 'role is required'
      },
      handleFieldChange: handleRoleChange
    }
  ];

  const orgCustomFormFields: FormFieldType[] = [
    {
      id: 'tenants',
      name: 'tenants',
      type: 'multi-select',
      label: 'Organization',
      options: organizationsList,
      itemValueKey: 'id',
      itemLabelKey: 'tenantName',
      handleFieldChange: handleInputChange
    }
  ];

  useEffect(() => {
    setAdminList(adminData);
  }, [adminData]);

  useEffect(() => {
    setOrganizationsList(organizationData);
  }, [organizationData]);

  useEffect(() => {
    setOpen(false);
    closeDialog();
    if (isAdminListUpdated) {
      dispatch(getAdminData());
      dispatch(setIsAdminListUpdated(false));
    }
  }, [isAdminListUpdated]);

  //methods
  const handleAddAdminButtonClick = () => {
    setInitialValues({
      email: '',
      name: '',
      phoneNo: '',
      role: ROLES.SUPER_ADMIN,
      tenants: []
    });
    setFields(baseCustomFormFields);
    setIsAddModal(true);
    setOpen(true);
  };

  const handleFilterAdminUsers = (value: string) => {
    const filterOrganizationData = adminData.filter(function (item) {
      return item.email.includes(value);
    });
    setAdminList(filterOrganizationData);
  };

  const handleDeleteIconClick = (email: string) => {
    setOpen(false);
    dispatch(deleteAdmin(email));
  };

  const handleClose = (value) => {
    setOpen(false);
  };

  const closeDialog = () => {
    setOpen(false);
  };

  //Methods
  const onCancel = () => {
    closeDialog();
  };

  const handleEditIconClick = (row) => {
    let formFields = [...baseCustomFormFields];
    formFields.map((field) => {
      field.isDisabled = true;
      return field;
    });

    if (row.role.id !== ROLES.SUPER_ADMIN) {
      formFields = [...formFields, ...orgCustomFormFields];
    }

    setFields(formFields);
    setIsAddModal(false);
    setInitialValues({
      email: row.email,
      name: row.name,
      phoneNo: row.phoneNo,
      role: row.role.id,
      tenants: row.tenants
    });
    setOpen(true);
  };

  const handleSwitchIconClick = (row) => {
    dispatch(
      updateAdminUserStatus({
        email: row.email,
        value: !row.isEnabled
      })
    );
  };

  const onSubmit = (values: any) => {
    setOpen(false);
    if (!isAddModal) {
      dispatch(
        updateAdminData({
          email: values.email,
          tenants: values.tenants.map((tenantObj) => tenantObj.id)
        })
      );
    } else {
      dispatch(
        addAdminUser({
          email: values.email,
          name: values.name,
          phoneNumber: values.phoneNumber,
          role: values.role,
          tenant: values.tenants.map((tenantObj) => tenantObj.id),
          is_enabled: true
        })
      );
    }
  };

  const handleViewOrientation = (
    _event: MouseEvent<HTMLElement>,
    newValue: string | null
  ) => {
    if (newValue !== null) {
      setSelectedTab(newValue);
    }
  };

  return (
    <>
      <Helmet>
        <title>BookingDLiteOrganazation</title>
      </Helmet>
      <PageTitleWrapper>
        <PageHeader
          heading={t('admin-management')}
          subHeading={t('admin-list-label')}
        >
          <Button
            size={'small'}
            sx={{ pt: 1, pb: 1 }}
            variant={'contained'}
            className="letter-spacing"
            startIcon={<AddTwoToneIcon fontSize="small" />}
            onClick={handleAddAdminButtonClick}
          >
            {t('add-admin')}
          </Button>
          <TextField
            size={'small'}
            id="outlined-search"
            label={t('search-admin-user')}
            type="search"
            onChange={(e) => handleFilterAdminUsers(e.target.value)}
          />
          <ToggleButtonGroup
            value={selectedTab}
            exclusive
            onChange={handleViewOrientation}
            size={'small'}
          >
            <ToggleButton disableRipple value={LIST_VIEW.CARD_VIEW}>
              <GridViewSharpIcon />
            </ToggleButton>
            <ToggleButton disableRipple value={LIST_VIEW.TABLE_VIEW}>
              <TableRowsTwoToneIcon />
            </ToggleButton>
          </ToggleButtonGroup>
        </PageHeader>
      </PageTitleWrapper>

      <Container maxWidth="lg">
        <Grid
          container
          direction="row"
          justifyContent="center"
          alignItems="stretch"
          spacing={3}
        >
          <Grid item xs={12}>
            <AdminManagementView
              handleDeleteIconClick={handleDeleteIconClick}
              handleSwitchIconClick={handleSwitchIconClick}
              inputAminUserData={adminList}
              handleEditIconClick={handleEditIconClick}
              inputSelectedTab={selectedTab}
            />
          </Grid>
        </Grid>
        <SimpleDialog
          keepMounted
          fullWidth
          scroll="paper"
          model_title={
            <Typography sx={{ p: 3, pl: 2 }} variant="h4" fontWeight="bold">
              {isAddModal ? t('add-admin') : t('edit-admin')}
            </Typography>
          }
          model_content={
            <Box>
              <CustomForm
                formFields={fields}
                initialValues={initialValues}
                submitBtnHandler={onSubmit}
                cancelBtnHandler={onCancel}
              />
            </Box>
          }
          open={open}
          onClose={handleClose}
        />
      </Container>
    </>
  );
};

export default AdminManagement;
